import React, { memo } from 'react';
import { useAppContext } from 'src/components/app-context';
import Loader from 'src/components/loader';
import { PageContent } from 'src/components/page';
import { useMatchedRoute } from 'src/hooks/use-matched-route';
import type {
  ProductCollectionSitemapQuery,
  SitemapCollection as SitemapCollectionType,
} from '../../types';
import HeaderBlock from './components/header-block';
import SitemapCollection from './components/sitemap-collections';
import { useProductCollectionSitemapPageQuery } from './queries/use-product-collection-sitemap-page';

import * as S from './styles';

type RequiredNotNull<T> = Required<{ [P in keyof T]: NonNullable<T[P]> }>;

export const ProductCollectionSitemapPageLoader = () => {
  const route = useMatchedRoute();
  const variables = { slug: route?.match?.params?.slug ?? '' };
  const { data, isLoading, isSuccess } =
    useProductCollectionSitemapPageQuery(variables);

  if (!isLoading && !isSuccess) {
    return null;
  }

  return (
    <PageContent withAds data-test="ProductCollectionSitemapPage">
      {isLoading ? <Loader /> : null}
      {isSuccess && data?.productCollectionSitemap ? (
        <ProductCollectionSitemapPage
          productCollectionSitemap={data.productCollectionSitemap}
        />
      ) : null}
    </PageContent>
  );
};

const ProductCollectionSitemapPage = ({
  productCollectionSitemap,
}: RequiredNotNull<
  Pick<ProductCollectionSitemapQuery, 'productCollectionSitemap'>
>) => {
  const { t } = useAppContext();
  const { sitemapCollection, name: title } = productCollectionSitemap;

  return (
    <S.SitemapWrapper>
      <HeaderBlock
        title={t('category', 'product-collection-sitemap.heading', { title })}
      />
      {Array.isArray(sitemapCollection)
        ? sitemapCollection
            .filter((obj): obj is SitemapCollectionType => obj !== undefined)
            .map(({ property, sitemapItems }) => (
              <SitemapCollection key={property} sitemapItems={sitemapItems} />
            ))
        : null}
    </S.SitemapWrapper>
  );
};

export default memo(ProductCollectionSitemapPageLoader);
